import * as _ from 'underscore';

import { isCountAggregate } from '../../../_common/platform/ts/AttrQueryValuesFormat';
import { ATTRIBUTE_TYPES } from '../../../_common/platform/ts/ConfigurationConstants';

export const chartConstants = {
    d3_constants: {
        d3_default_labelColor: '#000000', // "black";
        d3_default_labelFontSize: 10,
        d3_default_labelPositionHoriz: 'left',
        d3_default_labelPositionVert: 'top',
        d3_default_label_ShowInLabel: ['argument'],
        d3_default_labelInOneLine: true,
        d3_default_label_addSeriesName: false,
        d3_default_labelHideRadius: 25,
        d3_default_labelHideWidth: 30,
        d3_default_labelHideHeight: 14,
        d3_default_legendPosition: 'right',
        d3_default_showLegend: true,
        d3_default_useD3palette: false, //LL: 03.08.16: Leo suggested to drop these options:  always false

        d3_bubbleCompact_Name: 'bubbleMap',
        d3_treeMap_Name: 'areaMap'
    },

    chartParams: {},

    dataTypes: {
        undefDataType: 'undefined',
        arrCommonDataTypes: ['string', 'integer', 'real', 'date', 'datetime', 'time'],
        arrTimePartTypes: [
            'year',
            'yearFiscal',
            'halfyear',
            'halfYearFiscal',
            'quarter',
            'quarterFiscal',
            'month',
            'week',
            'weekday',
            'day',
            'hour',
            'minute',
            'second',
            'millisecond'
        ]
    },

    defaultAxisValue: 'default',
    defaultAxisDisplay: 'automatic',
    axisTypes: {
        s: ['default', 'discrete'],
        b: ['default', 'discrete'],
        e: ['default', 'discrete'],
        string: ['default', 'discrete'],
        i: ['default', 'discrete', 'continuous', 'logarithmic'],
        r: ['default', 'discrete', 'continuous', 'logarithmic'],
        integer: ['default', 'discrete', 'continuous', 'logarithmic'],
        real: ['default', 'discrete', 'continuous', 'logarithmic'],
        date: ['default', 'discrete', 'continuous'],
        datetime: ['default', 'discrete', 'continuous'],
        time: ['default', 'discrete', 'continuous'],
        timePart: ['default', 'discrete', 'continuous'],
        undef: ['default', 'discrete'],
        undefined: ['default', 'discrete']
    },

    //LL: used in CustomStyles:
    defaultPointLabelPosition: 'outside',
    defaultPointLabelRotationAngle: 0,
    defaultPointLabelColorBack: 'white',
    defaultPointLabelColorText: 'black',
    defaultPointLabelColorBorder: null
};

export const CHART_STRIPS = {
    stripType: ['arguments', 'values'],
    notUsedType: 'not used', //LL: 02.06.20
    labelVerticalAlignment: ['center', 'top', 'bottom'],
    labelHorizontalAlignment: ['center', 'left', 'right']
};

export const CHART_CONSTANT_LINES = {
    constantLineType: ['arguments', 'values'],
    notUsedType: 'not used', //LL: 02.06.20
    //dashStyle: ['dash', 'dot', 'longDash', 'dashdot', 'longDashdot', 'dashdashdot'],
    //dashStyle_display: ['dash', 'dot', 'longDash', 'dash-dot', 'longDash-dot', 'dash-dash-dot'],
    dashStyle: [
        { value: 'solid', display: 'solid' },
        { value: 'dash', display: 'dash' },
        { value: 'dot', display: 'dot' },
        { value: 'longDash', display: 'longDash' },
        { value: 'dashdot', display: 'dash-dot' },
        { value: 'longDashdot', display: 'longDash-dot' },
        { value: 'dashdashdot', display: 'dash-dash-dot' }
    ],
    labelVerticalAlignment: ['center', 'top', 'bottom'],
    labelHorizontalAlignment: ['center', 'left', 'right']
};

export const SERIES_POINT_SYMBOL = ['circle', 'square', 'polygon', 'triangleDown', 'triangleUp', 'cross'];

export const AXIS_LIST = {
    Y: 'Values(Y)', // 'Y - Values',
    Ya: 'Size of Area',
    Yb: 'Size of Bubble',
    X1: 'Arguments(X)', // 'X1 - Arguments',
    X1d: 'Arguments',
    X2: 'Side By Side', // 'X2 - Grouping',
    X4: 'Separate Lines', // analog X2 for Lines, Area - just grouping
    X4a: 'Separate Areas',
    X4s: 'Separate Series', // for Scatter
    X5: 'Separate Series', // analog X2 for D3 charts - just grouping
    X3: 'Stacked', // 'X3(Z) - Stacked',
    Z_Size: 'Size for bubble', // 'Z - Size for bubble'
    //'Y3': 'Values Stacked(Y)',

    XL: 'Argument Label',
    XC: 'Point Color',
    XP: 'Point Label',
    //'StackP': 'Stack Point Label',

    C2F: 'Click-to-filter Argument',
    T: 'Tooltip',
    SBT: 'Sub Titles',
    A: 'Attribute',

    Y_Open: 'Open Value', // for fiscal
    Y_Close: 'Close Value', // for fiscal
    Y_High: 'High Value', // for fiscal
    Y_Low: 'Low Value', // for fiscal
    Y_Min: 'Min Value', // for range
    Y_Max: 'Max Value' // for range
};

function isColorMappingForNumeric(objRow) {
    const forColorMappingForNumeric =
        objRow.axis === 'XC' &&
        (isCountAggregate(objRow) ||
            objRow.attrType === ATTRIBUTE_TYPES.INTEGER ||
            objRow.attrType === ATTRIBUTE_TYPES.REAL);

    return forColorMappingForNumeric;
}

function getPointDenomination(chartType: string) {
    if (chartType === chartConstants.d3_constants.d3_bubbleCompact_Name) return 'Bubble';
    if (chartType === chartConstants.d3_constants.d3_treeMap_Name) return 'Area';
    return '';
}

export function isD3chart(chartType: string) {
    return (
        chartType === chartConstants.d3_constants.d3_bubbleCompact_Name ||
        chartType === chartConstants.d3_constants.d3_treeMap_Name
    );
}

// For Validators
export function getVariatedAxisList(chartType) {
    let result = { ...AXIS_LIST };

    if (chartType === 'bar') {
        _.each(result, (key, obj) => {
            if (key === 'XC') {
                result[key] = 'Bar Color';
            }
            if (key === 'XP') {
                result[key] = 'Bar Label';
            }
        });
    }
    if (chartType === 'line' || chartType === 'spline' || chartType === 'stepline') {
        _.each(result, (key, obj) => {
            if (key === 'XC' && isColorMappingForNumeric(obj) !== true) {
                result[key] = 'Line Color';
            }
        });
    }
    if (chartType === 'area' || chartType === 'splinearea' || chartType === 'steparea') {
        _.each(result, (key, obj) => {
            if (key === 'XC' && isColorMappingForNumeric(obj) !== true) {
                result[key] = 'Area Color';
            }
        });
    } else if (chartType === 'bubble') {
        _.each(result, (key, obj) => {
            if (key === 'XC') {
                result[key] = 'Bubble Color';
            }
            if (key === 'XP') {
                result[key] = 'Bubble Label';
            }
        });
    } else if (chartType === 'pie' || chartType === 'doughnut') {
        _.each(result, (key, obj) => {
            if (key === 'XC') {
                result[key] = 'Sector Color';
            }
            if (key === 'XP') {
                result[key] = 'Sector Label';
            }
        });
    } else if (isD3chart(chartType)) {
        const pd = getPointDenomination(chartType);

        _.each(result, (key, obj) => {
            if (key === 'XC') {
                result[key] = pd + ' Color';
            }
            if (key === 'XP') {
                result[key] = pd + ' Label';
            }
        });
    }

    return result;
}

export const AXIS_UNIQUE_LIST = [
    'X1',
    'X1d',
    'X2',
    'X4',
    'X4a',
    'X4s',
    'X5',
    'X3',
    'Z_Size',
    'XL',
    'XP',
    'XC',
    'StackP',
    //'C2F', //LL: try multi (as 'A')
    'T',
    'SBT'
];
